import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setDogAge, setDogSize, setPetType, calculatePetAge, clearDogAge } from '../store/slices/ageSlice'
import '../pages/AgePage.css'

const PetAgeCalculatorPage = ({ tools = [] }) => {
    const dispatch = useDispatch()
    const { dogAge, dogSize, petType, dogResult } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])

    return (
        <ToolPageTemplate path="/pet-age-calculator/" tools={tools}>
            <div>
                <section className="age-calculator">
                    <div className="age-input-section">
                        <div className="age-item" style={{ marginBottom: '12px' }}>
                            <span className="age-label">Pet Type:</span>
                            <select value={petType} onChange={(e) => dispatch(setPetType(e.target.value))}>
                                <option value="dog">Dog</option>
                                <option value="cat">Cat</option>
                            </select>
                        </div>
                        <Input type="number" label="Pet's Age (years)" value={dogAge} onChange={(e) => dispatch(setDogAge(e.target.value))} min="0" step="0.1" />
                        {petType === 'dog' && (
                            <div className="age-item" style={{ marginTop: '12px' }}>
                                <span className="age-label">Breed Size:</span>
                                <select value={dogSize} onChange={(e) => dispatch(setDogSize(e.target.value))}>
                                    <option value="small">Small (up to 20 lbs)</option>
                                    <option value="medium">Medium (21-50 lbs)</option>
                                    <option value="large">Large (51-90 lbs)</option>
                                    <option value="giant">Giant (90+ lbs)</option>
                                </select>
                            </div>
                        )}
                        <div className="age-buttons">
                            <Button onClick={() => dispatch(calculatePetAge())} disabled={!dogAge}>Calculate Pet Age</Button>
                            <Button variant="secondary" onClick={() => dispatch(clearDogAge())}>Clear</Button>
                        </div>
                    </div>

                    {dogResult && (
                        <ResultBox title="Human Age Equivalent">
                            <div className="age-item">
                                <span className="age-label">Your {petType}'s age in human years:</span>
                                <span className="age-value">{dogResult.humanAge}</span>
                            </div>
                        </ResultBox>
                    )}
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use Pet Age Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Choose Pet Type</h3>
                                    <p>Select whether you're calculating age for a dog or a cat.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Enter Pet's Age</h3>
                                    <p>Input your pet's age in years (decimals allowed, e.g. 2.5 for two and a half years).</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>Select Breed Size (Dogs Only)</h3>
                                    <p>For dogs, choose the breed size category since larger breeds age faster in adulthood.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>Unlike the outdated "1 dog year = 7 human years" myth, real pet aging is non-linear — pets mature rapidly in their first two years, then age more gradually. Our calculator uses breed-size-adjusted formulas for dogs and a separate curve for cats, giving you a far more realistic human-age equivalent for understanding your pet's life stage and health needs.</p>
                    </div>

                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            <div className="faq-item">
                                <h3>Why is the "multiply by 7" dog age rule wrong?</h3>
                                <p>Dogs age faster in their first two years, and body size affects aging speed dramatically. A 1-year-old dog is closer to 15 in human years, and a Great Dane ages much faster than a Chihuahua. The ×7 rule ignores both factors.</p>
                            </div>
                            <div className="faq-item">
                                <h3>How accurate is this pet age calculator?</h3>
                                <p>It uses breed- and size-adjusted formulas from the American Veterinary Medical Association (AVMA) and the 2020 epigenetic clock study from UC San Diego. More accurate than the classic ×7 rule for both dogs and cats.</p>
                            </div>
                            <div className="faq-item">
                                <h3>How do you convert dog years to human years?</h3>
                                <p>The first two dog years ≈ 24 human years. After that, each dog year adds roughly 4–5 human years for small breeds, 5–6 for medium, and 6–7 for large and giant breeds.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Do cats age differently from dogs?</h3>
                                <p>Yes. A cat's first year ≈ 15 human years, the second adds 9 more, then each subsequent cat year ≈ 4 human years. Cats don't need breed-size adjustment — the formula is the same for all breeds.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Is the pet age calculator free?</h3>
                                <p>Yes. Free, no signup required, no ads interrupting the result. All calculations run locally in your browser — no data is uploaded or stored.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your own exact age</div>
                            </Link>
                            <Link to="/blog/how-to-tell-a-dogs-age-by-its-teeth/" className="related-tool">
                                <div className="tool-icon">How to Tell a Dog's Age by Its Teeth</div>
                                <div className="tool-description">A vet-informed guide to estimating your dog's age from dental signs</div>
                            </Link>
                            <Link to="/generation-calculator/" className="related-tool">
                                <div className="tool-icon">Generation Calculator</div>
                                <div className="tool-description">A fun way to see which generation you belong to</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default PetAgeCalculatorPage